import { Spacing } from '@persona-id/design-tokens';
import { ChangeEventValue, Coords } from 'google-map-react';
import { ComponentProps, ReactNode, useCallback, useMemo, useState } from 'react';
import { SizeMe } from 'react-sizeme';
import styled from 'styled-components';

import { CircleDrawableMap, MapMarker } from 'dashboard/components/Map';
import Marker from 'dashboard/components/Map/Marker';
import Pagination from 'dashboard/components/Pagination';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { stringifyLatitude, stringifyLongitude } from 'dashboard/lib/utils/stringFormat';
import { List, ListStatus } from 'dashboard/models/list';
import { ListItem, ListItemGeolocation } from 'dashboard/models/list-item';
import { User } from 'dashboard/models/user';
import { COLORS } from 'dashboard/styles/variables';
import ButtonTableNullState from 'dashboard/views/DashboardListsShowView/ButtonTableNullState';
import GeolocationListItemsTable from 'dashboard/views/DashboardListsShowView/GeolocationListItemsTable';
import Card from 'design-system/components/Card';
import { BASE } from 'design-system/variables/spacing';
import { usePlaceSearch } from 'lib/hooks/map/usePlaceSearch';
import useSet from 'lib/hooks/useSet';
import { PaginationParams } from 'lib/interfaces/api';
import { Place } from 'lib/interfaces/place';
import Size from 'lib/interfaces/size';
import {
  computeCenterAndZoomForPlace,
  computeInitialMapCenterAndZoom,
  serializePlace,
} from 'lib/utils/maps';

const GEOLOCATION_LIST_MAP_HEIGHT = 428;

const MapContainer = styled('div')`
  flex: none;
  height: ${GEOLOCATION_LIST_MAP_HEIGHT}px;
  margin: 0 ${BASE * -3}px ${Spacing[7]};
`;

const SecondaryTooltipLabel = styled('p')`
  color: ${({ theme }) => theme.color.primary.brand};
`;

interface GeolocationPageContent {
  list: List;
  listItems: ListItemGeolocation[];
  users: User[];
  paginationParams: PaginationParams;
  sortParams: SortParams;
  pageSize: number;
  isLoading: boolean;
  addListItemButtonEnabled: boolean;
  removeListItem: (listItem: ListItem) => void;
  handleShowAddListItemModal: () => void;
  handleDrawCircleStart: () => void;
  handleDrawCircleCancel: () => void;
  handleDrawCircleComplete: (place: Place) => void;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handleSortUpdate: (sortParams: SortParams) => void;
  filterSection: ReactNode;
}

const GeolocationPageContent = ({
  list,
  listItems,
  users,
  paginationParams,
  sortParams,
  pageSize,
  isLoading,
  addListItemButtonEnabled,
  removeListItem,
  handleShowAddListItemModal,
  handleDrawCircleStart,
  handleDrawCircleCancel,
  handleDrawCircleComplete,
  handlePageLinkClick,
  handlePageSizeClick,
  handleSortUpdate,
  filterSection,
}: GeolocationPageContent) => {
  const listActive = list.attributes.status === ListStatus.Active;

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [mapSize, updateMapSize] = useState<Size>(null);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [mapSearchResult, setMapSearchResult] = useState<{ place: Place; address: string }>(null);
  const { add: activateMarker, delete: deactivateMarker, has: isMarkerActive } = useSet<string>();

  const updateMapSearchResult = (place: Place, address: string) => {
    setMapSearchResult({ place, address });
  };

  const renderMarkerTooltip = useCallback((marker: MapMarker) => {
    const { primaryTooltipLabel, secondaryTooltipLabel } = marker;
    return (
      <>
        {primaryTooltipLabel && <p>{primaryTooltipLabel}</p>}
        {secondaryTooltipLabel && (
          <SecondaryTooltipLabel>{secondaryTooltipLabel}</SecondaryTooltipLabel>
        )}
      </>
    );
  }, []);

  const handleTableRowOnMouseOver = (place: Place) => {
    activateMarker(serializePlace(place));
  };

  const handleTableRowOnMouseOut = (place: Place) => {
    deactivateMarker(serializePlace(place));
  };

  const handleMapChange = useCallback(({ center: { lat, lng }, zoom }: ChangeEventValue) => {
    updateMapCenter({ lat, lng });
    updateMapZoom(zoom);
  }, []);

  const handleMarkerClick = useCallback(
    (_markerIdx: number, { lat, lng, radiusMeters }: ComponentProps<typeof Marker>) => {
      const { center, zoom } = computeCenterAndZoomForPlace(mapSize, {
        lat: lat,
        lng: lng,
        radiusMeters: radiusMeters,
      });

      updateMapCenter(center);
      updateMapZoom(zoom);
    },
    [mapSize],
  );

  const listItemMarkers = useMemo<MapMarker[]>(
    () =>
      // filtering redacted list items since their latitude, longitude, and radiusMeters is set to null
      listItems
        .filter((listItem) => !listItem.attributes['redacted-at'])
        .map((listItem) => MapMarker.fromListItemGeolocation(listItem))
        .filter((marker): marker is MapMarker => marker != null)
        .map((marker) => {
          return new MapMarker({
            ...marker,
            focusedMarkerColor: COLORS.red100,
            defaultMarkerColor: COLORS.gray70,
          });
        }),
    [listItems],
  );

  const markers = useMemo<MapMarker[]>(() => {
    if (mapSearchResult != null) {
      const mapSearchResultMarker: MapMarker = {
        ...mapSearchResult.place,
        focusedMarkerColor: COLORS.red100,
        defaultMarkerColor: COLORS.gray70,
        primaryTooltipLabel: mapSearchResult.address
          ? mapSearchResult.address
          : `${stringifyLatitude(mapSearchResult.place.lat)}, ${stringifyLongitude(
              mapSearchResult.place.lng,
            )}`,
      };

      return [...listItemMarkers, mapSearchResultMarker];
    } else {
      return listItemMarkers;
    }
  }, [listItemMarkers, mapSearchResult]);

  const { center: initialCenter, zoom: initialZoom } = useMemo(
    () => computeInitialMapCenterAndZoom(GEOLOCATION_LIST_MAP_HEIGHT, listItemMarkers),
    [listItemMarkers],
  );

  const [mapCenter, updateMapCenter] = useState<Coords>(initialCenter);
  const [mapZoom, updateMapZoom] = useState<number>(initialZoom);

  const placeSearchCallbacks = usePlaceSearch(updateMapSearchResult, handleMapChange);

  return (
    <>
      <SizeMe monitorHeight>
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        {({ size }: { size: Size }) => {
          // Only want to updateMapSize when its different or else it'll infinitely re-render
          if (mapSize == null || mapSize.width !== size.width || mapSize.height !== size.height) {
            updateMapSize({ width: size.width, height: size.height });
          }

          return (
            <MapContainer>
              <CircleDrawableMap
                markers={markers}
                center={mapCenter}
                zoom={mapZoom}
                onMarkerClick={handleMarkerClick}
                renderTooltip={renderMarkerTooltip}
                isMarkerActive={(place: Place) => isMarkerActive(serializePlace(place))}
                handleDrawCircleStart={handleDrawCircleStart}
                handleDrawCircleCancel={handleDrawCircleCancel}
                handleDrawCircleComplete={handleDrawCircleComplete}
                mapOptions={{
                  controlSize: 48,
                  // Full screen breaks a lot of interactions (e.g. tooltip); I believe it's because its canvas is separated from
                  // the rest of our React App; Not sure how to fix this so just going to disable it since its broken
                  fullscreenControl: false,
                }}
                {...placeSearchCallbacks}
              />
            </MapContainer>
          );
        }}
      </SizeMe>

      {listActive && filterSection}

      <Card>
        <Card.Body>
          <Card.Title>List items</Card.Title>
          <GeolocationListItemsTable
            sortParams={sortParams}
            listActive={listActive}
            listItems={listItems as ListItemGeolocation[]}
            users={users}
            isLoading={isLoading}
            removeListItem={removeListItem}
            handleTableRowOnMouseOver={handleTableRowOnMouseOver}
            handleTableRowOnMouseOut={handleTableRowOnMouseOut}
            handleTableRowClicked={(place: Place) => {
              if (mapSize) {
                const { center, zoom } = computeCenterAndZoomForPlace(mapSize, place);

                updateMapCenter(center);
                updateMapZoom(zoom);
              }
            }}
            handleSortUpdate={handleSortUpdate}
            nullState={
              <ButtonTableNullState
                headerText='No items listed'
                dataTestBlock='email-address-table'
                active={list.attributes.status === ListStatus.Active}
                activeText='To add a new location, click on the pencil to draw on the map, or add an item using GPS coordinates'
                inactiveText='This list contains no active items as it was archived'
                buttonEnabled={addListItemButtonEnabled}
                handleButtonClick={handleShowAddListItemModal}
              />
            }
          />
          {listItems.length > 0 && (
            <Pagination
              pageSize={pageSize}
              paginationParams={paginationParams}
              handlePageSizeClick={handlePageSizeClick}
              handlePageLinkClick={handlePageLinkClick}
            />
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default GeolocationPageContent;
